import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import RichEditor from '../util/RichEditor';

interface Translation { subject: string; body: string }
interface Template {
  id: number;
  template_key: string;
  name: string;
  description?: string;
  variables: string[];
  translations: Record<string, Translation>;
}
interface Lang { id: number; iso_639_1: string; native_name: string; name: string }

interface Props { baseUrl?: string }

const NotificationTemplates: React.FC<Props> = ({ baseUrl = '' }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [languages, setLanguages] = useState<Lang[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [lang, setLang] = useState<string>('en');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const selected = templates.find(x => x.template_key === selectedKey);

  const load = useCallback(async () => {
    try {
      const [tpl, lng] = await Promise.all([
        axios.get('mail-template'),
        axios.get('static-language/public/list'),
      ]);
      setTemplates(tpl.data);
      setLanguages(lng.data);
      if (tpl.data.length && !selectedKey) setSelectedKey(tpl.data[0].template_key);
    } catch {
      // ошибки авторизации обрабатывает глобальный interceptor
    }
  }, [selectedKey]);

  useEffect(() => { load(); }, [load]);

  // При смене шаблона/языка — подставляем контент
  useEffect(() => {
    if (!selected) return;
    const tr = selected.translations?.[lang];
    setSubject(tr?.subject ?? '');
    setBody(tr?.body ?? '');
  }, [selectedKey, lang, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`mail-template/${selected.template_key}`, { lang, subject, body });
      setTemplates(prev => prev.map(x => x.template_key === data.template_key ? data : x));
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const insertVar = (v: string) => setBody(prev => `${prev} {{${v}}}`);

  const langHasContent = (tpl: Template, code: string) =>
    !!tpl.translations?.[code]?.body || !!tpl.translations?.[code]?.subject;

  return (
    <div className="flex gap-[14px] h-full text-[var(--text-color)]" style={{ minHeight: 0 }}>
      {/* LEFT — список шаблонов */}
      <div
        className="bg-[var(--btn-user-form)] rounded-[8px] border border-[var(--border-input)] overflow-y-auto"
        style={{ width: 260, flexShrink: 0 }}
      >
        {templates.map(tpl => {
          const filled = languages.filter(l => langHasContent(tpl, l.iso_639_1)).length;
          return (
            <button
              key={tpl.template_key}
              type="button"
              onClick={() => setSelectedKey(tpl.template_key)}
              className={`w-full text-left px-[14px] py-[10px] border-b border-[var(--border-input)] cursor-pointer ${
                selectedKey === tpl.template_key ? 'bg-[var(--active-row-color)]' : 'bg-transparent hover:bg-[var(--btn-user-in)]'
              }`}
            >
              <div className="text-[13px] font-semibold">{tpl.name}</div>
              <div className="text-[11px] text-[var(--text-color-calendar-week)]">
                {tpl.template_key} · {filled}/{languages.length} {t('Languages') || 'langs'}
              </div>
            </button>
          );
        })}
      </div>

      {/* RIGHT — редактор */}
      {selected ? (
        <div className="flex-1 flex flex-col min-h-0 bg-[var(--btn-user-form)] rounded-[8px] border border-[var(--border-input)] p-[14px]">
          <div className="flex items-center justify-between mb-[10px]">
            <div>
              <div className="text-[15px] font-bold">{selected.name}</div>
              {selected.description && (
                <div className="text-[11px] text-[var(--text-color-calendar-week)]">{selected.description}</div>
              )}
            </div>
            {/* Язык */}
            <select
              className="timesheet-edit-input"
              value={lang}
              onChange={e => setLang(e.target.value)}
              style={{ minWidth: 180, cursor: 'pointer' }}
            >
              {languages.map(l => (
                <option key={l.id} value={l.iso_639_1}>
                  {l.native_name}{langHasContent(selected, l.iso_639_1) ? '  ✓' : ''}
                </option>
              ))}
            </select>
          </div>

          {lang !== 'en' && !langHasContent(selected, lang) && (
            <div className="text-[11px] mb-[8px] px-[10px] py-[6px] rounded-[5px] bg-[var(--bg-alert)] text-[var(--text-alert)]">
              {t('TemplateFallbackHint') || 'Empty — English version will be used as fallback.'}
            </div>
          )}

          {/* Subject */}
          <label className="text-[10px] font-semibold mb-[3px] uppercase tracking-wider text-[var(--text-color-calendar-week)]">{t('Subject') || 'Subject'}</label>
          <input
            className="timesheet-edit-input w-full mb-[10px]"
            type="text"
            value={subject}
            placeholder="Subject"
            onChange={e => setSubject(e.target.value)}
          />

          {/* Переменные */}
          {selected.variables?.length > 0 && (
            <div className="flex flex-wrap gap-[6px] mb-[10px]">
              {selected.variables.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVar(v)}
                  className="bg-[var(--btn-user-in)] text-[var(--text-color)] border border-[var(--border-input)] rounded-[5px] px-[8px] py-[2px] text-[12px] cursor-pointer hover:bg-[var(--btn-user-hover)]"
                  title={`Insert {{${v}}}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 min-h-0 mb-[10px]">
            <RichEditor value={body} onChange={setBody} baseUrl={baseUrl} fillHeight />
          </div>

          <div>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? '…' : t('Save') || 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-[var(--text-color-calendar-week)]">
          {t('SelectTemplate') || 'Select a template'}
        </div>
      )}
    </div>
  );
};

export default NotificationTemplates;
